import { Crosshair, Minus, PencilLine, Plus, StickyNote, Target, TrendingUp, type LucideIcon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  MOBILE_SHEET_CLASSES,
} from '@/components'
import type {
  WorkbenchChange,
  WorkbenchChangeKind,
  WorkbenchChangeStatus,
} from '@/components/Workbench/chartContext'
import { cn } from '@/lib/utils'
import type { WorkbenchUpdateSummary } from '../workbenchUpdate'

const KIND_ICON: Record<WorkbenchChangeKind, LucideIcon> = {
  idea: Target,
  setup: Target,
  marker: Crosshair,
  drawing: TrendingUp,
  note: StickyNote,
  unknown: PencilLine,
}

const GROUPS: Array<{ status: WorkbenchChangeStatus; title: string; icon: LucideIcon; tone: string }> = [
  { status: 'added', title: 'Added', icon: Plus, tone: 'text-success' },
  { status: 'edited', title: 'Edited', icon: PencilLine, tone: 'text-warning' },
  { status: 'removed', title: 'Removed', icon: Minus, tone: 'text-danger' },
]

function ChangeItem({ change }: { change: WorkbenchChange }) {
  const Icon = KIND_ICON[change.kind] ?? PencilLine
  const removed = change.status === 'removed'
  return (
    <li className="rounded-md border border-border bg-card px-3 py-2">
      <div className="flex items-center gap-2">
        <Icon size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted-foreground" />
        <span className={cn('text-label text-foreground', removed && 'line-through text-muted-foreground')}>
          {change.title}
        </span>
      </div>
      {change.fields.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pl-[22px]">
          {change.fields.map((f, i) => {
            const changed = f.before !== undefined
            // An edit spotlights what moved; the rest recedes.
            const muted = removed || (change.status === 'edited' && !changed)
            return (
              <div key={`${f.label}-${i}`} className="contents">
                <dt className="text-caption text-muted-foreground">{f.label}</dt>
                <dd className={cn('text-caption break-words', muted ? 'text-muted-foreground' : 'text-foreground')}>
                  {changed ? (
                    <>
                      <span className="line-through text-muted-foreground">{f.before}</span>
                      <span aria-hidden> → </span>
                      <span className="sr-only"> changed to </span>
                      <span className="font-medium">{f.value}</span>
                    </>
                  ) : (
                    f.value
                  )}
                </dd>
              </div>
            )
          })}
        </dl>
      )}
    </li>
  )
}

interface WorkbenchChangesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  summary: WorkbenchUpdateSummary
  /** A sent update reads in the past tense. */
  sent?: boolean
}

/**
 * What changed on the chart, for the person: grouped Added / Edited / Removed,
 * each element with its fields, and an edit's moved fields shown before → after.
 * Read-only — shared by the composer's pending tab and a sent update's card.
 */
export default function WorkbenchChangesDialog({ open, onOpenChange, summary, sent = false }: WorkbenchChangesDialogProps) {
  const title = summary.first ? (sent ? 'Chart sent to AI' : 'Your chart') : sent ? 'Workbench changes sent' : 'Workbench changes'
  const chartLabel = summary.label.replace(/ chart$/, '')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-lg', MOBILE_SHEET_CLASSES)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {chartLabel}
            {summary.first ? ' · everything on the chart' : ' · since the AI last saw it'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60dvh] overflow-y-auto -mx-1 px-1 flex flex-col gap-4">
          {GROUPS.map((g) => {
            const items = summary.changes.filter((c) => c.status === g.status)
            if (items.length === 0) return null
            const GroupIcon = g.icon
            return (
              <section key={g.status} aria-label={g.title} className="flex flex-col gap-2">
                {!summary.first && (
                  <h3 className={cn('flex items-center gap-1.5 text-eyebrow uppercase', g.tone)}>
                    <GroupIcon size={12} strokeWidth={2} aria-hidden />
                    {g.title}
                  </h3>
                )}
                <ul className="flex flex-col gap-2">
                  {items.map((c, i) => (
                    <ChangeItem key={`${c.title}-${i}`} change={c} />
                  ))}
                </ul>
              </section>
            )
          })}
          {summary.omitted > 0 && (
            <p className="text-caption text-muted-foreground">
              …and {summary.omitted} more {summary.omitted === 1 ? 'change' : 'changes'}.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
